import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Missing unsubscribe token.');
      return;
    }

    const unsubscribe = async () => {
      try {
        const res = await api.get(`/unsubscribe/${token}`);
        setStatus('success');
        setMessage(res.data.message);
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Unsubscribe failed. The link may be invalid.');
      }
    };

    unsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="glass border-white/20 dark:border-white/10">
          <CardHeader className="text-center">
            {status === 'processing' && (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="w-12 h-12 text-primary animate-spin" />
                </div>
                <CardTitle className="font-['Manrope']">Processing</CardTitle>
                <CardDescription>Unsubscribing you from emails...</CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <div className="flex justify-center mb-4">
                  <CheckCircle className="w-12 h-12 text-emerald-500" />
                </div>
                <CardTitle className="font-['Manrope'] text-emerald-600 dark:text-emerald-400">Unsubscribed</CardTitle>
                <CardDescription>{message}</CardDescription>
              </>
            )}
            {status === 'error' && (
              <>
                <div className="flex justify-center mb-4">
                  <XCircle className="w-12 h-12 text-destructive" />
                </div>
                <CardTitle className="font-['Manrope'] text-destructive">Error</CardTitle>
                <CardDescription>{message}</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="flex justify-center">
            {status !== 'processing' && (
              <Button onClick={() => navigate('/')} data-testid="unsubscribe-home-btn">
                Go to Brioritizer
              </Button>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
